import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

import { queryKeys } from '~/api/queryKeys';
import {
  GOOGLE_PLATFORM,
  META_PLATFORM,
  type ScheduledEnrichmentPlatform,
} from '~/features/content-enrichment/config/scheduled-enrichment-platform';
import { fetchScheduledJobStatus } from '~/features/content-enrichment/services/scheduled-enrichment.service';
import { useScheduledGoogleStore } from '~/features/content-enrichment/store/scheduled-google.store';
import { useScheduledMetaStore } from '~/features/content-enrichment/store/scheduled-meta.store';
import { compactEnrichmentRunConfig } from '~/features/content-enrichment/types/enrichment-run-config';
import type { ScheduledJobStatusResponse } from '~/features/content-enrichment/types/scheduled-enrichment.types';
import { FUNNEL_STEP } from '~/features/content-enrichment/utils/phase-funnel-mapping';

/** URL suffix appended to `{contentEnrichmentBase}/{batchId}` per step. */
const STEP_SUFFIX: Record<
  | typeof FUNNEL_STEP.CURRENT_SCORE
  | typeof FUNNEL_STEP.REVIEW
  | typeof FUNNEL_STEP.FULL_CATALOG
  | typeof FUNNEL_STEP.OVERVIEW
  | typeof FUNNEL_STEP.DELIVERY,
  string
> = {
  [FUNNEL_STEP.CURRENT_SCORE]: '',
  [FUNNEL_STEP.REVIEW]: '/review',
  [FUNNEL_STEP.FULL_CATALOG]: '/full',
  [FUNNEL_STEP.OVERVIEW]: '/overview',
  [FUNNEL_STEP.DELIVERY]: '/delivery',
};

/** Lowercase stage-status keywords that indicate active execution. */
const IN_PROGRESS_STAGE_STATUSES = new Set([
  'in_progress',
  'running',
  'active',
  'processing',
  'started',
]);

/** Lowercase stage-status keywords that indicate successful completion. */
const COMPLETED_STAGE_STATUSES = new Set(['completed', 'complete', 'done', 'success', 'succeeded']);

type ResumableStep =
  | typeof FUNNEL_STEP.CURRENT_SCORE
  | typeof FUNNEL_STEP.REVIEW
  | typeof FUNNEL_STEP.FULL_CATALOG
  | typeof FUNNEL_STEP.OVERVIEW
  | typeof FUNNEL_STEP.DELIVERY;

/**
 * Determines the deepest active funnel step from a job-status snapshot.
 *
 * Execution sequence: contentScoring → sampleEnrichment → pipeline.
 * Higher-order phases win (pipeline > sampleEnrichment > contentScoring).
 * Within the pipeline block, inner stage states refine the target:
 *
 *   DELIVERY in_progress            → DELIVERY step  (polling page)
 *   ENRICHMENT completed
 *     OR pipeline AWAITING_APPROVAL → OVERVIEW step  (approve/publish CTA)
 *   pipeline present otherwise      → FULL_CATALOG   (progress polling)
 *   sampleEnrichment present        → REVIEW         (review & trigger pipeline)
 *   contentScoring (any state)      → CURRENT_SCORE  (loading gate handles in-progress)
 */
function resolveTargetStep(job: ScheduledJobStatusResponse): ResumableStep {
  const { pipeline, sampleEnrichment } = job;

  if (pipeline) {
    const stages = pipeline.stages ?? [];

    // DELIVERY in progress → user watches delivery polling page.
    const deliveryStage = stages.find((s) => s.name === 'DELIVERY');
    if (deliveryStage && IN_PROGRESS_STAGE_STATUSES.has(deliveryStage.status.toLowerCase())) {
      return FUNNEL_STEP.DELIVERY;
    }

    // ENRICHMENT stage completed OR overall pipeline awaiting approval → Enrichment Overview.
    const enrichmentStage = stages.find((s) => s.name === 'ENRICHMENT');
    const enrichmentDone =
      enrichmentStage != null && COMPLETED_STAGE_STATUSES.has(enrichmentStage.status.toLowerCase());
    if (enrichmentDone || pipeline.status === 'AWAITING_APPROVAL') {
      return FUNNEL_STEP.OVERVIEW;
    }

    // Pipeline exists but hasn't reached ENRICHMENT completion → Full Catalog polling.
    return FUNNEL_STEP.FULL_CATALOG;
  }

  // sampleEnrichment present (any state) → Review & Refine; the page itself shows
  // the loader when status is not COMPLETED.
  if (sampleEnrichment) {
    return FUNNEL_STEP.REVIEW;
  }

  // Content scoring present or not yet started → step 0. The layout's loading gate
  // shows the enrichment progress view while scoring is not yet COMPLETED, then
  // reveals the score data once it is.
  return FUNNEL_STEP.CURRENT_SCORE;
}

/**
 * Handles navigation from the catalog-operations batch list into a staged
 * scheduled-enrichment funnel (Meta Ads or Google Ads) when a batch is
 * in-progress.
 *
 * On `resume(batchId, platform)`:
 *   1. Fetches `GET /enrichments/scheduled/jobs/{jobId}/status`
 *   2. Seeds the TanStack Query cache with the response — the adapter's
 *      `useQuery({ queryKey: jobStatus(batchId) })` reads it immediately on
 *      mount, so the layout's loading gate sees the real status on the first
 *      render (no flash of the progress view for completed runs)
 *   3. Hydrates the platform's Zustand store as a belt-and-suspenders fallback
 *   4. Navigates to the deepest active funnel step under the platform's routes
 *
 * Both platform stores are subscribed up-front (rules of hooks); `platform`
 * selects the config + hydrate target at call time.
 * Returns `resumingId` so the caller can render a per-row loading indicator.
 */
export function useScheduledBatchResume() {
  const [resumingId, setResumingId] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hydrateMeta = useScheduledMetaStore((s) => s.hydrate);
  const hydrateGoogle = useScheduledGoogleStore((s) => s.hydrate);

  const resume = useCallback(
    async (batchId: string, platform: ScheduledEnrichmentPlatform['platform']) => {
      if (resumingId) return;
      setResumingId(batchId);
      try {
        const config = platform === 'meta' ? META_PLATFORM : GOOGLE_PLATFORM;
        const hydrate = platform === 'meta' ? hydrateMeta : hydrateGoogle;

        const job = await fetchScheduledJobStatus(batchId);

        // Pre-populate the adapter's jobStatus query cache so the layout
        // receives the correct status on the first render without an async round
        // trip. This is what prevents the loading screen from flashing when
        // contentScoring is already COMPLETED.
        queryClient.setQueryData(queryKeys.scheduledEnrichment.jobStatus(job.jobId), job);

        // Belt-and-suspenders: also write to the Zustand store so the adapter's
        // per-workflow polling hooks (useScheduledTriggerStatus) start with the
        // correct workflow IDs immediately, even before the useQuery above resolves
        // through the normal React hook path.
        //
        // Inference rule: execution sequence is contentScoring → sampleEnrichment
        // → pipeline. If a later phase is present, all earlier phases must have
        // COMPLETED — even when the backend omits the earlier phase block from
        // the response. Without this, contentScoringInitialStatus stays null when
        // contentScoring is absent, scoringReady = false, and the layout shows the
        // loading screen despite sampleEnrichment already being COMPLETED.
        const laterPhasesStarted = !!(job.sampleEnrichment ?? job.pipeline);
        const pipelineStarted = !!job.pipeline;

        // Restore the run configuration from the job-status echo so a resume in a
        // different browser recovers the operator's original attribute choice —
        // the delivery panel reads this to offer only what the run enriched.
        // Omitted (leaving any persisted value intact) when the echo carried no
        // config, e.g. a batch created before the backend added it.
        const runConfig = compactEnrichmentRunConfig({
          ...(job.contentAttributes ? { contentAttributes: job.contentAttributes } : {}),
          ...(job.searchDescriptors ? { searchDescriptors: job.searchDescriptors } : {}),
          ...(job.scoring ? { scoring: job.scoring } : {}),
          ...(job.retailer ? { retailer: job.retailer } : {}),
          ...(job.targetLocales?.length ? { targetLocales: job.targetLocales } : {}),
        });

        hydrate({
          scheduledJobId: job.jobId,
          contentScoringWorkflowId: job.contentScoring?.workflowId ?? null,
          sampleEnrichmentWorkflowId: job.sampleEnrichment?.workflowId ?? null,
          pipelineWorkflowId: job.pipeline?.workflowId ?? null,
          syncId: job.syncId,
          filters: job.filters ?? null,
          connectionId: null,
          contentScoringInitialStatus:
            job.contentScoring?.status ?? (laterPhasesStarted ? 'COMPLETED' : null),
          sampleEnrichmentInitialStatus:
            job.sampleEnrichment?.status ?? (pipelineStarted ? 'COMPLETED' : null),
          pipelineInitialStatus: job.pipeline?.status ?? null,
          ...(Object.keys(runConfig).length > 0 ? { runConfig } : {}),
        });

        const step = resolveTargetStep(job);
        const path = `${config.contentEnrichmentBase}/${job.jobId}${STEP_SUFFIX[step]}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        void navigate({ to: path as any });
      } finally {
        setResumingId(null);
      }
    },
    [resumingId, navigate, queryClient, hydrateMeta, hydrateGoogle],
  );

  return { resume, resumingId };
}
